import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/ui/date-picker";
import { UploadMultiFiles } from "@/components/ui/upload-multi-files";
import AutocompleteSelect from "@/components/ui/autocomplete-select";
import { Link, Euro, Plus, Trash, Ticket } from "lucide-react";
import { FlightActivity } from "../models/activity.model";
import { getFlightCompanies } from "@/components/utils/flight-companies";
import { getAirports } from "@/components/utils/airports";
import { useI18n } from "@/hooks/useI18n";
import ActivityModalShell from "./ActivityModalShell";
import ActivityDetailView from "./ActivityDetailView";

type FlightTab = 'details' | 'documents';

interface FlightModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Partial<FlightActivity>) => void;
  icon?: React.ReactNode;
  initialData?: Partial<FlightActivity>;
  itineraryId: string;
  itineraryStartDate?: string | null;
  itineraryEndDate?: string | null;
  defaultDate?: string;
  /** Sola lettura: campi non interagibili, footer con solo "Chiudi" (usato dalla Libreria). */
  readOnly?: boolean;
}

const FlightModal = ({ isOpen, onClose, onSave, icon, initialData, itineraryId, itineraryStartDate, itineraryEndDate, defaultDate, readOnly = false }: FlightModalProps) => {
  const { t } = useI18n();

  // Generate temporary UUID for new activities
  const [activityId] = useState<string>(() =>
    initialData?.id || crypto.randomUUID()
  );

  const [formData, setFormData] = useState<Partial<FlightActivity>>({
    id: activityId,
    include_price: true,
    title: '',
    link: '',
    address: '',
    travels: [
      {
        flightNumber: '',
        airline: '',
        link: '',
        from: '',
        to: '',
        fromDate: undefined,
        fromTime: '',
        toDate: undefined,
        toTime: ''
      }
    ],
    cost: 0,
    description: '',
    documentation: []
  });

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({ ...initialData, id: activityId });
    }
  }, [initialData, activityId]);

  useEffect(() => {
    if (isOpen && !initialData && defaultDate) {
      setFormData(prev => ({
        ...prev,
        travels: prev.travels?.map((t, i) => i === 0 ? { ...t, fromDate: defaultDate, toDate: defaultDate } : t) || prev.travels
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [activeTab, setActiveTab] = useState<FlightTab>('details');

  useEffect(() => {
    if (!isOpen) {
      setShowValidationBanner(false);
      setErrors({});
      setActiveTab('details');
    }
  }, [isOpen]);

  // Create options for AutocompleteSelect components
  const airlineOptions = getFlightCompanies().map(company => ({
    value: company,
    label: company
  }));

  const airportOptions = getAirports().map(airport => ({
    value: airport,
    label: airport
  }));

  const validateForm = (): Record<string, boolean> => {
    const requiredFields = ['cost', 'description'];
    const newErrors: Record<string, boolean> = {};

    requiredFields.forEach(field => {
      const value = formData[field as keyof FlightActivity];
      if (field === 'cost') {
        newErrors[field] = value === null || value === undefined || (value as number) < 0;
      } else {
        newErrors[field] = !value || (typeof value === 'string' && value.trim() === '');
      }
    });

    if (!formData.travels || formData.travels.length === 0) {
      newErrors.travels = true;
    } else {
      formData.travels.forEach((travel, index) => {
        const travelRequiredFields = ['flightNumber', 'airline', 'from', 'to', 'fromDate', 'fromTime', 'toDate', 'toTime'];
        travelRequiredFields.forEach(field => {
          const value = travel[field as keyof typeof travel];
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors[`travel_${index}_${field}`] = true;
          }
        });

        if (travel.from && travel.to && travel.from.trim() === travel.to.trim()) {
          newErrors[`travel_${index}_from`] = true;
          newErrors[`travel_${index}_to`] = true;
        }
      });
    }

    setErrors(newErrors);
    return newErrors;
  };

  const getDateWithOffset = (dateString: string | null | undefined, offset: number) => {
    if (!dateString) return undefined;
    const baseDate = new Date(dateString);
    if (Number.isNaN(baseDate.getTime())) return undefined;
    baseDate.setDate(baseDate.getDate() + offset);
    return baseDate;
  };

  const fromMinDate = getDateWithOffset(itineraryStartDate, -1);
  const fromMaxDate = getDateWithOffset(itineraryEndDate, 1);
  const toMinDate = getDateWithOffset(itineraryStartDate, -1);
  const toMaxDate = getDateWithOffset(itineraryEndDate, 1);

  const addFlight = () => {
    setFormData(prev => ({
      ...prev,
      travels: [
        ...(prev.travels || []),
        {
          flightNumber: '',
          airline: '',
          link: '',
          from: '',
          to: '',
          fromDate: '',
          fromTime: '',
          toDate: '',
          toTime: ''
        }
      ]
    }));
  };

  const removeFlight = (index: number) => {
    setFormData(prev => ({
      ...prev,
      travels: prev.travels?.filter((_, i) => i !== index) || []
    }));
  };

  const updateFlight = (index: number, field: keyof FlightActivity['travels'][0], value: string) => {
    setFormData(prev => ({
      ...prev,
      travels: prev.travels?.map((flight, i) => {
        if (i !== index) return flight;

        const updatedFlight = { ...flight, [field]: value };

        // se sto settando fromDate controllo che toDate sia dopo, altrimenti la resetto
        if (field === 'fromDate' && updatedFlight.toDate) {
          const fromDateValue = new Date(value);
          const toDateValue = new Date(updatedFlight.toDate);
          if (!Number.isNaN(fromDateValue.getTime()) && !Number.isNaN(toDateValue.getTime()) && toDateValue < fromDateValue) {
            updatedFlight.toDate = '';
          }
        }

        return updatedFlight;
      }) || []
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (!Object.values(newErrors).some(error => error)) {
      const title = formData.travels[0].from + ' → ' + formData.travels[0].to;

      onSave({
        ...formData,
        title,
        type: 'flight' as const,
        date: formData.travels[0].fromDate
      });
      onClose();
      return;
    }
    setShowValidationBanner(true);
    setActiveTab('details');
  };

  const hasDetailsError = Object.keys(errors).some(key => errors[key]);

  if (readOnly) {
    return (
      <ActivityDetailView
        isOpen={isOpen}
        onClose={onClose}
        icon={icon}
        title={t('builder.activities.flight')}
        activity={formData}
      />
    );
  }

  return (
    <ActivityModalShell
      isOpen={isOpen}
      onClose={onClose}
      icon={icon}
      title={t('builder.activities.flight')}
      formId="flight-form"
      onSubmit={handleSubmit}
      showValidationBanner={showValidationBanner}
      activeTab={activeTab}
      onActiveTabChange={(value) => setActiveTab(value as FlightTab)}
      tabs={[
        {
          key: 'details',
          label: t('modals.tabDetails'),
          hasError: hasDetailsError,
          content: (
            <>
              {/* Flights Section */}
              <div className="flex flex-col items-center justify-center gap-4">
                {formData.travels?.map((travel, index) => (
                  <Card key={index} className="border border-gray-200 w-full">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="font-medium">{t('modals.flightN', { n: index + 1 })}</CardTitle>
                        {!readOnly && formData.travels && formData.travels.length > 1 && index !== 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFlight(index)}
                            className=""
                          >
                            <Trash className="h-4 w-4" /> {t('modals.deleteFlight')}
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Flight Number, Airline, Link */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="relative">
                          <Ticket className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                          <Input
                            value={travel.flightNumber}
                            onChange={(e) => updateFlight(index, 'flightNumber', e.target.value)}
                            placeholder={t('modals.flightNumber')}
                            className="pl-10"
                            hasError={errors[`travel_${index}_flightNumber`]}
                            readOnly={readOnly}
                          />
                        </div>
                        <AutocompleteSelect
                          options={airlineOptions}
                          value={travel.airline}
                          onValueChange={(value) => updateFlight(index, 'airline', value)}
                          placeholder={t('modals.airline')}
                          searchPlaceholder={t('modals.searchAirlines')}
                          emptyMessage="No airlines found"
                          manual={true}
                          filterMode="includes"
                          maxWidth="320px"
                          hasError={errors[`travel_${index}_airline`]}
                          disabled={readOnly}
                        />
                        <div className="relative">
                          <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                          <Input
                            value={travel.link}
                            onChange={(e) => updateFlight(index, 'link', e.target.value)}
                            placeholder={t('modals.link')}
                            className="pl-10"
                            hasError={errors[`travel_${index}_link`]}
                            readOnly={readOnly}
                          />
                        </div>
                      </div>

                      {/* From, FromDate, FromTime */}
                      <div className="space-y-4">
                        <div className="flex items-center gap-4 flex-wrap">
                          <div className="w-14 font-medium">{t('common.from')}</div>
                          <div className="flex-1">
                            <AutocompleteSelect
                              options={airportOptions}
                              value={travel.from}
                              onValueChange={(value) => updateFlight(index, 'from', value)}
                              placeholder={t('modals.departureAirport')}
                              searchPlaceholder={t('modals.searchAirports')}
                              emptyMessage="No airports found"
                              manual={true}
                              filterMode="includes"
                              maxWidth="360px"
                              hasError={errors[`travel_${index}_from`]}
                              disabled={readOnly}
                            />
                          </div>
                          <div className="flex flex-row gap-4">
                            <div className="w-52 flex-0">
                              <DatePicker
                                value={travel.fromDate}
                                onSelect={(date) => updateFlight(index, 'fromDate', date)}
                                placeholder={t('modals.departureDate')}
                                hasError={errors[`travel_${index}_fromDate`]}
                                minDate={fromMinDate}
                                maxDate={fromMaxDate}
                                isDisabled={readOnly}
                              />
                            </div>
                            <div className="w-24">
                              <Input
                                type="time"
                                value={travel.fromTime}
                                onChange={(e) => updateFlight(index, 'fromTime', e.target.value)}
                                placeholder="HH:MM"
                                hasError={errors[`travel_${index}_fromTime`]}
                                readOnly={readOnly}
                              />
                            </div>
                          </div>
                        </div>

                        {/* To, ToDate, ToTime */}
                        <div className="flex items-center gap-4 flex-wrap">
                          <div className="w-14 font-medium">{t('common.to')}</div>
                          <div className="flex-1 flex-shrink">
                            <AutocompleteSelect
                              options={airportOptions}
                              value={travel.to}
                              onValueChange={(value) => updateFlight(index, 'to', value)}
                              placeholder={t('modals.arrivalAirport')}
                              searchPlaceholder={t('modals.searchAirports')}
                              emptyMessage="No airports found"
                              manual={true}
                              filterMode="includes"
                              maxWidth="360px"
                              hasError={errors[`travel_${index}_to`]}
                              disabled={readOnly}
                            />
                          </div>
                          <div className="flex flex-row gap-4">
                            <div className="w-52">
                              <DatePicker
                                value={travel.toDate}
                                onSelect={(date) => updateFlight(index, 'toDate', date)}
                                placeholder={t('modals.arrivalDate')}
                                hasError={errors[`travel_${index}_toDate`]}
                                minDate={travel.fromDate ? travel.fromDate : toMinDate}
                                maxDate={toMaxDate}
                                isDisabled={readOnly}
                              />
                            </div>
                            <div className="w-24">
                              <Input
                                type="time"
                                value={travel.toTime}
                                onChange={(e) => updateFlight(index, 'toTime', e.target.value)}
                                placeholder="HH:MM"
                                hasError={errors[`travel_${index}_toTime`]}
                                readOnly={readOnly}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Add Flight Button */}
                {!readOnly && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={addFlight}
                    className="w-fit"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t('modals.addFlight')}
                  </Button>
                )}
              </div>

              {/* Price Card */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{t('modals.price')}</CardTitle>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">{t('modals.includePrice')}</span>
                      <Switch
                        checked={formData.include_price || false}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_price: checked }))}
                        disabled={readOnly}
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-center gap-4">
                    <div className="flex-1">
                      <div className="relative">
                        <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                        <Input
                          id="cost"
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.cost}
                          onChange={(e) => setFormData(prev => ({ ...prev, cost: parseFloat(e.target.value) }))}
                          placeholder={t('modals.amountOnlyVisibleToYou')}
                          className="pl-10"
                          hasError={errors.cost}
                          readOnly={readOnly}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Note Card */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('modals.note')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <RichTextEditor
                    value={formData.description || ''}
                    onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                    placeholder={t('modals.shareClientPreferencesFlight')}
                    hasError={errors.description}
                    readOnly={readOnly}
                  />
                </CardContent>
              </Card>
            </>
          ),
        },
        {
          key: 'documents',
          label: t('modals.tabDocuments'),
          content: (
            <Card>
              <CardHeader>
                <CardTitle>{t('modals.documentation')}</CardTitle>
              </CardHeader>
              <CardContent>
                <UploadMultiFiles
                  files={formData.documentation || []}
                  onFilesChange={(files) => setFormData(prev => ({ ...prev, documentation: files }))}
                  label={t('modals.uploadBoardingPass')}
                  maxFiles={5}
                  itineraryId={itineraryId}
                  activityId={activityId}
                  readOnly={readOnly}
                />
              </CardContent>
            </Card>
          ),
        },
      ]}
    />
  );
};

export default FlightModal;
